import React, { useState } from 'react';
import './OptimizedImage.css';

/**
 * OptimizedImage component with lazy loading, alt text, and SEO optimization
 */
const OptimizedImage = ({ 
  src, 
  webpSrc,  // Add WebP support
  alt, 
  className = '', 
  width, 
  height, 
  loading = 'lazy',
  sizes,
  srcSet,
  webpSrcSet,  // Add WebP srcSet support
  placeholder = '/images/placeholder.svg',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  // Ensure alt text is provided for accessibility and SEO
  const altText = alt || 'Billede fra daginstitution sammenligning';

  // Auto-generate WebP only for local assets where a .webp sibling is expected.
  // Remote URLs (e.g. blob storage) must pass webpSrc explicitly or omit WebP.
  const autoWebpSrc = (() => {
    if (webpSrc === false || webpSrc === null) return null;
    if (webpSrc) return webpSrc;
    if (!src || /^https?:\/\//i.test(src)) return null;
    return src.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  })();
  const autoWebpSrcSet = webpSrcSet ?? (srcSet && !/^https?:\/\//i.test(srcSet)
    ? srcSet.replace(/\.(jpg|jpeg|png)/gi, '.webp')
    : null);

  return (
    <div className={`optimized-image-container ${className}`}>
      {!isLoaded && (
        <div className="image-placeholder">
          <div className="loading-spinner" aria-label="Indlæser billede..." />
        </div>
      )}
      
      <picture>
        {/* WebP format for modern browsers */}
        {autoWebpSrc && (
          <source 
            srcSet={autoWebpSrcSet || autoWebpSrc} 
            sizes={sizes}
            type="image/webp" 
          />
        )}
        
        {/* Fallback for browsers that don't support WebP */}
        <img
          src={hasError ? placeholder : src}
          srcSet={srcSet}
          alt={altText}
          width={width}
          height={height}
          loading={loading}
          sizes={sizes}
          className={`optimized-image ${isLoaded ? 'loaded' : ''} ${hasError ? 'error' : ''}`}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      </picture>
      
      {hasError && (
        <div className="image-error" aria-label="Billede kunne ikke indlæses">
          <span>📷</span>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
